import { Button, Tooltip } from '@ibsys/kit';
import { PencilIcon, PlusIcon, StarIcon } from 'lucide-react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { DashboardPreset } from '@/entities/dashboards';
import { useDashboardPresets } from '@/entities/settings';
import { cn } from '@/shared/lib';
import { ConfirmationButton } from '@/shared/ui';

type Props = {
  /**
   * Применённый дашборд сняли (удалили): страница возвращается к стандартному, а если она была
   * открыта разделом этого дашборда — уходит и с его адреса.
   */
  onActivePresetChange: (presetId: string | null) => void;
  /** Завести новый дашборд: страница включает режим редактирования на пустой сетке */
  onCreatePreset: () => void;
  /** Править состав применённого дашборда: страница включает режим редактирования на его сетке */
  onEditPreset: (preset: DashboardPreset) => void;
};

/**
 * Действия над применённым дашбордом: завести новый, редактировать, закрепить в меню, удалить.
 * Без применённого дашборда остаётся только заведение нового — править и закреплять нечего.
 */
export const DashboardPresetActions: FC<Props> = ({ onActivePresetChange, onCreatePreset, onEditPreset }) => {
  const { t } = useTranslation('qualityDashboard');
  const { activePreset, remove, setPinned } = useDashboardPresets();

  const handleDelete = useCallback(() => {
    if (!activePreset) return;
    remove(activePreset.id);
    onActivePresetChange(null);
  }, [activePreset, remove, onActivePresetChange]);

  const pinLabel = activePreset?.pinned ? t('presets.unpinFromMenu') : t('presets.pinToMenu');

  return (
    <div className="flex items-center gap-1">
      <Tooltip tooltip={t('presets.create')}>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('presets.create')}
          icon={<PlusIcon className="size-4" />}
          onClick={onCreatePreset}
        />
      </Tooltip>

      {activePreset && (
        <>
          <Tooltip tooltip={t('presets.edit')}>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('presets.edit')}
              icon={<PencilIcon className="size-4" />}
              onClick={() => onEditPreset(activePreset)}
            />
          </Tooltip>
          <Tooltip tooltip={pinLabel}>
            <Button
              variant="ghost"
              size="icon"
              aria-label={pinLabel}
              aria-pressed={activePreset.pinned}
              icon={<StarIcon className={cn('size-4', activePreset.pinned && 'fill-current')} />}
              onClick={() => setPinned(activePreset.id, !activePreset.pinned)}
            />
          </Tooltip>
          <ConfirmationButton
            title={t('presets.deleteConfirmTitle', { name: activePreset.name })}
            description={t('presets.deleteConfirmDescription')}
            onConfirm={handleDelete}
            trigger={{ size: 'icon', 'aria-label': t('presets.delete') }}
          />
        </>
      )}
    </div>
  );
};
